/**
 * Validator for ServerConfiguration
 *
 * Validates configuration data loaded from CDN and returns a properly typed ServerConfiguration.
 */

import type { LegacyRule, Rules, TranslatedRule } from '@/configuration/translateRules';
import type {
  Alignment,
  AnalyticsAttribution,
  Border,
  BorderRadius,
  ChatMessageConfig,
  CloseButton,
  DisplayRule,
  DisplayRules,
  DisplayType,
  FontLibrary,
  GAConfiguration,
  InitialChatButton,
  MinimizedChatButton,
  OnboardingConfig,
  OngoingChatConfig,
  PrivacyDisclaimer,
  ProactiveChatCampaign,
  ProactiveChatConfig,
  SelfServiceConfig,
  ServerConfiguration,
  Theme,
  ThemeColors,
  ThemeFormats,
  ThrottlingConfig,
  WidgetType,
} from '@/domain/configuration';
import {
  ALIGNMENTS,
  BORDER_RADII,
  CLOSE_ICON_TYPE,
  DISPLAY_TYPE,
  INITIAL_BUTTON_TYPES,
  RULE_TYPES,
  WIDGET_TYPES,
} from '@/domain/configuration';
import {
  DISPLAY_RULE_BAR,
  DISPLAY_RULE_BUTTON,
  QUICK_ACTION_TYPES,
  type QuickAction,
  V2_BORDER_RADII,
  type V2Appearance,
  type V2Beacon,
  type V2Behavior,
  type V2Border,
  type V2Colors,
  type V2Configuration,
  type V2DisplayRuleContent,
  type V2DisplayRules,
  type V2EndChatMessages,
  type V2Fonts,
  type V2Format,
  type V2Header,
  type V2Input,
  type V2InputChatWindow,
  type V2LauncherButton,
  type V2Messages,
  type V2Tooltips,
} from '@/domain/configurationV2';
import { getDefaultServerV2, getDefaultV2Configuration } from '@/domain/defaultV2Configuration';
import { DISPLAY_RULE_MODES, type DisplayRuleMode, LAUNCHER_TYPES } from '@/domain/launcherTypes';
import { type V2Launcher, type V2LauncherDisplayRule, type V2LauncherDisplayRules } from '@/launchers/types';
import { URL_MATCH_TYPES } from '@/lib/displayRules/urlDisplayRuleMatcher';
import { logger } from '@/lib/logger';
import { asRecord } from '@/lib/utils/isRecord';
import {
  among,
  array,
  boolean,
  field,
  number,
  object,
  optionalField,
  record,
  string,
  text,
  validate,
} from '@/lib/utils/validator/validator';

const KNOWN_LAUNCHER_TYPES = new Set<string>(Object.values(LAUNCHER_TYPES));
const KNOWN_DISPLAY_RULE_MODES = new Set<string>(DISPLAY_RULE_MODES);

// Only optionality is stripped before the object test: a nullable field keeps its
// null, so a partial is rejected where a null default would be replaced wholesale.
type DeepPartial<T> = {
  [K in keyof T]?: Exclude<T[K], undefined> extends readonly unknown[]
    ? T[K]
    : Exclude<T[K], undefined> extends object
      ? DeepPartial<Exclude<T[K], undefined>>
      : T[K];
};

/**
 * Deep-merges a partial object into defaults. When a key is missing or undefined
 * in partial, the default value is kept. Nested plain objects are merged
 * recursively; arrays and primitives in partial replace the default.
 *
 * Type assertions are required for the generic recursive merge to satisfy
 * TypeScript; runtime behavior is type-safe.
 *
 * Exported for unit tests.
 */
/* eslint-disable @typescript-eslint/consistent-type-assertions -- generic deep merge */
export function deepMerge<T extends object>(defaults: T, partial: DeepPartial<T> | null | undefined): T {
  if (partial == null) {
    return defaults;
  }
  const result = { ...defaults };
  for (const key of Object.keys(partial) as (keyof T)[]) {
    const pVal = partial[key];
    if (pVal === undefined) {
      continue;
    }
    const dVal = result[key];
    if (
      dVal !== null &&
      typeof dVal === 'object' &&
      !Array.isArray(dVal) &&
      pVal !== null &&
      typeof pVal === 'object' &&
      !Array.isArray(pVal)
    ) {
      result[key] = deepMerge(dVal, pVal) as T[typeof key];
    } else {
      result[key] = pVal as T[typeof key];
    }
  }
  return result as T;
}
/* eslint-enable @typescript-eslint/consistent-type-assertions */

/**
 * Validates configuration data and returns a properly typed ServerConfiguration.
 * This validator handles the full structure including all nested types.
 */
export function validServerConfiguration(value: unknown): ServerConfiguration {
  return validate(value, serverConfigurationValidator);
}

function serverConfigurationValidator(input: unknown): ServerConfiguration {
  return object(input, () => {
    // Required ServerConfiguration fields
    const clusterUrl = field('clusterUrl', string);
    const gladlySite = field('gladlySite', string);
    const loginDomain = field('loginDomain', string);
    const orgId = field('orgId', string);
    const providerId = field('providerId', string);
    // const appId = field("appId", string);

    // Optional ServerConfiguration fields
    const gladlyStage = optionalField('gladlyStage', string);

    // Required WidgetConfiguration fields
    const alignment = field('alignment', validAlignment);
    const border = field('border', validBorder);
    const borderRadius = field('borderRadius', validBorderRadius);
    const colors = field('colors', record(string));
    // const font = field("font", string);
    const zIndex = field('zIndex', number);

    const initialChatButton = field('initialChatButton', validInitialChatButton);
    const minimizedChatButton = field('minimizedChatButton', validMinimizedChatButton);
    const closeButton = field('closeButton', validCloseButton);
    const display = field('display', validDisplayType);
    const displayRules = field('displayRules', validDisplayRules);
    // const alwaysShow = field("alwaysShow", boolean);
    const onboardingValue = optionalField('onboarding', validOnboardingConfigPartial);
    const onboarding = mergeOnboardingConfig(onboardingValue);
    const ongoingChatValue = optionalField('ongoingChat', validOngoingChatConfigPartial);
    const ongoingChat = mergeOngoingChatConfig(ongoingChatValue);
    const chatClosedValue = optionalField('chatClosed', validChatMessageConfigPartial);
    const chatClosed = mergeChatMessageConfig(chatClosedValue, getDefaultChatClosedConfig());
    const chatThrottledValue = optionalField('chatThrottled', validChatMessageConfigPartial);
    const chatThrottled = mergeChatMessageConfig(chatThrottledValue, getDefaultChatThrottledConfig());
    const attachmentsEnabled = field('attachmentsEnabled', boolean);
    const widgetType = optionalField('widgetType', validWidgetType) ?? WIDGET_TYPES.CHAT;
    const selfService = field('selfService', validSelfServiceConfig);
    const throttling = field('throttling', validThrottlingConfig);
    const lng = field('lng', string);

    // Optional WidgetConfiguration fields
    const customText = optionalField('customText', validCustomText);
    const theme = optionalField('theme', validTheme);
    const proactiveChat = optionalField('proactiveChat', validProactiveChatConfig);
    const showPoweredByGladly = optionalField('showPoweredByGladly', boolean);
    const privacyDisclaimer = optionalField('privacyDisclaimer', validPrivacyDisclaimer);
    const analyticsAttribution = optionalField('analyticsAttribution', validAnalyticsAttribution);
    const audienceId = optionalField('audienceId', string);
    const provider = optionalField('provider', string);
    const providerConfig = optionalField('providerConfig', record(unknownLike));
    const rules = optionalField('rules', validRules);
    const v2 = optionalField('v2', validV2Configuration) ?? getDefaultServerV2();
    const v2Enabled = optionalField('v2Enabled', boolean) ?? false;

    return {
      // ServerConfiguration required fields
      clusterUrl,
      gladlySite,
      loginDomain,
      orgId,
      providerId,

      // ServerConfiguration optional fields
      gladlyStage,

      // WidgetConfiguration required fields
      border,
      borderRadius,
      colors,
      zIndex,
      alignment,
      // font,
      initialChatButton,
      minimizedChatButton,
      closeButton,
      display,
      displayRules,
      // alwaysShow,
      onboarding,
      ongoingChat,
      chatClosed,
      chatThrottled,
      attachmentsEnabled,
      widgetType,
      selfService,
      throttling,
      lng,

      // Optional fields
      customText,
      theme,
      proactiveChat,
      showPoweredByGladly,
      privacyDisclaimer,
      analyticsAttribution,
      audienceId,
      provider,
      providerConfig,
      rules,
      v2,
      v2Enabled,
    };
  });
}

/** Exported for unit tests. */
export function validV2Configuration(value: unknown): V2Configuration {
  return object(value, () => ({
    appearance: field('appearance', validV2Appearance),
    beacon: optionalField('beacon', validV2Beacon),
    behavior: field('behavior', validV2Behavior),
    endChatMessages: optionalField('endChatMessages', validV2EndChatMessages),
    launchers: optionalField('launchers', sanitizeLaunchers),
    legacyUI: optionalField('legacyUI', boolean),
    quickActions: optionalField('quickActions', sanitizeQuickActions),
    tooltips: optionalField('tooltips', validV2Tooltips),
  }));
}

/**
 * Validates the launcher envelope with per-entry fault isolation: a malformed
 * entry is dropped with a warning instead of being reported as a validation
 * issue, so one bad launcher can never fail the whole ServerConfiguration and
 * force an org-wide V1 fallback. This is deliberately hand-rolled rather than
 * built from the `object`/`field` combinators — those report into the shared
 * validation issue stream, which would invalidate the entire config.
 *
 * Display-rule contents are validated here; `payload` is not.
 */
export function sanitizeLaunchers(value: unknown): V2Launcher[] {
  if (!Array.isArray(value)) {
    logger.warn('[launchers] ignoring launchers config: expected an array', { received: typeof value });
    return [];
  }

  const launchers: V2Launcher[] = [];
  const seenIds = new Set<string>();
  let seenChatBar = false;

  for (const entry of value) {
    const launcher = sanitizeLauncherEntry(entry);
    if (!launcher) {
      continue;
    }
    if (seenIds.has(launcher.id)) {
      logger.warn(`[launchers] dropping duplicate launcher id "${launcher.id}" (keeping first)`);
      continue;
    }
    // One chat bar per org, so the readers downstream can take the only entry
    // instead of arbitrating between rival rule sets.
    if (launcher.type === LAUNCHER_TYPES.CHAT_BAR) {
      if (seenChatBar) {
        logger.warn(`[launchers] dropping extra CHAT_BAR entry "${launcher.id}" (keeping first)`);
        continue;
      }
      seenChatBar = true;
    }
    seenIds.add(launcher.id);
    launchers.push(launcher);
  }

  return launchers;
}

function sanitizeLauncherEntry(entry: unknown): V2Launcher | null {
  if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
    logger.warn('[launchers] dropping malformed launcher entry: not an object', { entry });
    return null;
  }

  /* eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- narrowing an unknown CDN entry */
  const obj = entry as Record<string, unknown>;
  const { id, type } = obj;

  if (typeof id !== 'string' || id.trim().length === 0) {
    logger.warn('[launchers] dropping malformed launcher entry: missing or blank "id"', { id });
    return null;
  }
  if (typeof type !== 'string') {
    logger.warn(`[launchers] dropping malformed launcher entry "${id}": missing or invalid "type"`, { type });
    return null;
  }
  if (!KNOWN_LAUNCHER_TYPES.has(type)) {
    return null;
  }

  const launcher: V2Launcher = { id, type };

  // On the pre-fetched path these are V1's own objects: read them behind a guard so a
  // hostile accessor drops this entry instead of failing the whole config.
  try {
    if (obj.displayRules !== undefined) {
      const displayRules = sanitizeLauncherDisplayRules(id, obj.displayRules);
      if (!displayRules) {
        return unusableDisplayRulesFallback(launcher);
      }
      launcher.displayRules = displayRules;
    }
    if (obj.payload !== undefined) {
      launcher.payload = structuredClone(obj.payload);
    }
  } catch (error) {
    logger.warn(`[launchers] launcher "${id}": displayRules or payload could not be read`, error);
    return unusableDisplayRulesFallback(launcher);
  }
  return launcher;
}

/**
 * Validates quick actions with the same per-entry fault isolation as
 * `sanitizeLaunchers`: a malformed or unknown entry is dropped with a warning so
 * it can never fail the whole ServerConfiguration.
 */
export function sanitizeQuickActions(value: unknown): QuickAction[] {
  if (!Array.isArray(value)) {
    logger.warn('[quickActions] ignoring quickActions config: expected an array', { received: typeof value });
    return [];
  }

  const quickActions: QuickAction[] = [];
  for (const entry of value) {
    const quickAction = sanitizeQuickActionEntry(entry);
    if (quickAction) {
      quickActions.push(quickAction);
    }
  }
  return quickActions;
}

function sanitizeQuickActionEntry(entry: unknown): QuickAction | null {
  const record = asRecord(entry);
  const answerId = asRecord(record?.payload)?.answerId;
  if (
    !record ||
    typeof record.id !== 'string' ||
    record.id.trim().length === 0 ||
    typeof record.label !== 'string' ||
    record.label.trim().length === 0 ||
    record.type !== QUICK_ACTION_TYPES.ANSWER ||
    typeof answerId !== 'string' ||
    answerId.trim().length === 0
  ) {
    logger.warn('[quickActions] dropping malformed quick action', { entry });
    return null;
  }
  return { id: record.id, label: record.label, payload: { answerId }, type: record.type };
}

// Dropping a launcher hides it — except CHAT_BAR, whose absence hands the chat bar
// to the gen-2 behavior.displayRules fallback instead, showing it wherever that
// fallback is silent. So the entry stays, gated by an allow-list that matches
// nothing: the same fail-safe hidden every other unusable rule set gets.
function unusableDisplayRulesFallback(launcher: V2Launcher): V2Launcher | null {
  if (launcher.type !== LAUNCHER_TYPES.CHAT_BAR) {
    return null;
  }
  logger.warn(`[launchers] hiding chat bar "${launcher.id}" everywhere: its display rules are unusable`);
  return { ...launcher, displayRules: { mode: 'SHOW_ON_MATCH', rules: [] } };
}

// A missing or unrecognized mode makes the whole set unusable; there is no default polarity.
function sanitizeLauncherDisplayRules(launcherId: string, value: unknown): V2LauncherDisplayRules | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    logger.warn(`[launchers] unusable display rules for "${launcherId}": displayRules is not an object`, {
      displayRules: value,
    });
    return null;
  }

  /* eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- narrowing an unknown CDN entry */
  const { mode, rules } = value as Record<string, unknown>;

  if (!isDisplayRuleMode(mode)) {
    logger.warn(
      `[launchers] unusable display rules for "${launcherId}": displayRules.mode must be ${DISPLAY_RULE_MODES.join(' or ')}`,
      { mode },
    );
    return null;
  }
  if (rules === undefined) {
    return { mode };
  }
  if (!Array.isArray(rules)) {
    logger.warn(`[launchers] unusable display rules for "${launcherId}": displayRules.rules is not an array`, {
      rules,
    });
    return null;
  }

  const contents: V2LauncherDisplayRule[] = [];
  for (const rule of rules) {
    const content = sanitizeLauncherDisplayRule(launcherId, rule);
    // One unusable rule discards the whole set: a partial list matches fewer pages, so a
    // HIDE launcher would appear on a page it excluded.
    if (!content) {
      return null;
    }
    contents.push(content);
  }
  return { mode, rules: contents };
}

function sanitizeLauncherDisplayRule(launcherId: string, rule: unknown): V2LauncherDisplayRule | null {
  if (typeof rule !== 'object' || rule === null || Array.isArray(rule)) {
    logger.warn(`[launchers] unusable display rules for "${launcherId}": display rule is not an object`, { rule });
    return null;
  }

  /* eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- narrowing an unknown CDN entry */
  const { display, displayDelay, matchType, value } = rule as Record<string, unknown>;

  if (typeof matchType !== 'string' || !URL_MATCH_TYPES.has(matchType)) {
    logger.warn(`[launchers] unusable display rules for "${launcherId}": unknown display rule matchType`, {
      matchType,
    });
    return null;
  }
  // A blank value matches every page under CONTAINS.
  if (typeof value !== 'string' || value.trim().length === 0) {
    logger.warn(`[launchers] unusable display rules for "${launcherId}": display rule value is missing or blank`, {
      value,
    });
    return null;
  }
  if (matchType === RULE_TYPES.REGEX && !compilesAsRegex(value)) {
    logger.warn(`[launchers] unusable display rules for "${launcherId}": display rule REGEX does not compile`, {
      value,
    });
    return null;
  }

  const sanitized: V2LauncherDisplayRule = { matchType, value };
  // An unrecognised choice is dropped rather than the rule: the rule still says where the
  // launcher shows, and falling back to the set's own polarity is what a build predating
  // the field does with the same config. Dropping the rule would instead move the launcher
  // onto or off a page the org excluded.
  if (display === DISPLAY_RULE_BUTTON || display === DISPLAY_RULE_BAR) {
    sanitized.display = display;
  } else if (display !== undefined) {
    logger.warn(`[launchers] ignoring an unknown display choice for "${launcherId}"`, { display });
  }
  if (displayDelay !== undefined) {
    // An unusable delay shows the launcher immediately rather than dropping the rule.
    sanitized.displayDelay =
      typeof displayDelay === 'number' && Number.isFinite(displayDelay) && displayDelay >= 0 ? displayDelay : 0;
  }
  return sanitized;
}

function isDisplayRuleMode(value: unknown): value is DisplayRuleMode {
  return typeof value === 'string' && KNOWN_DISPLAY_RULE_MODES.has(value);
}

// Same flags the matcher applies, so anything accepted here compiles there.
function compilesAsRegex(pattern: string): boolean {
  try {
    RegExp(pattern, 'gi');
    return true;
  } catch {
    return false;
  }
}

function validV2Beacon(value: unknown): V2Beacon {
  return object(value, () => ({
    manifestUrl: field('manifestUrl', string),
  }));
}

function validV2Appearance(value: unknown): V2Appearance {
  const defaultApp = getDefaultV2Configuration().appearance;
  return object(value, () => ({
    alignment: optionalField('alignment', string) ?? defaultApp.alignment,
    border: deepMerge(defaultApp.border, optionalField('border', validV2Border)),
    colors: deepMerge(defaultApp.colors, optionalField('colors', validV2Colors)),
    dropShadow: optionalField('dropShadow', boolean) ?? defaultApp.dropShadow,
    fonts: deepMerge(defaultApp.fonts, optionalField('fonts', validV2Fonts)),
    format: deepMerge(defaultApp.format, optionalField('format', validV2Format)),
    header: deepMerge(defaultApp.header, optionalField('header', validV2Header)),
    input: deepMerge(defaultApp.input, optionalField('input', validV2Input)),
    launcherButton: deepMerge(defaultApp.launcherButton, optionalField('launcherButton', validV2LauncherButton)),
    messages: deepMerge(defaultApp.messages, optionalField('messages', validV2Messages)),
    privacyDisclaimer: deepMerge(
      defaultApp.privacyDisclaimer,
      optionalField('privacyDisclaimer', validPrivacyDisclaimer),
    ),
    showPoweredByGladly: optionalField('showPoweredByGladly', boolean) ?? defaultApp.showPoweredByGladly,
    // Passed through unresolved: nothing reads it at this seam, and the push seam that
    // does owns the default so it lives in exactly one place.
    sidebarCollapseAt: optionalField('sidebarCollapseAt', number),
    zIndex: optionalField('zIndex', number) ?? defaultApp.zIndex,
  }));
}

function validV2Behavior(value: unknown): V2Behavior {
  const defaultBeh = getDefaultV2Configuration().behavior;
  return object(value, () => ({
    attachmentsEnabled: optionalField('attachmentsEnabled', boolean) ?? defaultBeh.attachmentsEnabled,
    displayRules: deepMerge(defaultBeh.displayRules, optionalField('displayRules', validV2DisplayRules)),
    gaConfiguration: optionalField('gaConfiguration', validGAConfiguration),
    lng: optionalField('lng', string) ?? defaultBeh.lng,
    provider: optionalField('provider', string),
    providerConfig: optionalField('providerConfig', record(unknownLike)),
    throttling: deepMerge(defaultBeh.throttling, optionalField('throttling', validThrottlingConfig)),
  }));
}

function validV2DisplayRules(value: unknown): V2DisplayRules {
  const defaultRules = getDefaultV2Configuration().behavior.displayRules;
  return object(value, () => ({
    rules: optionalField('rules', array(validDisplayRule)) ?? defaultRules.rules,
    showByDefault: optionalField('showByDefault', boolean) ?? defaultRules.showByDefault,
  }));
}

function validV2Colors(value: unknown): V2Colors {
  return object(value, () => ({
    accent: optionalField('accent', string),
    accentColor: optionalField('accentColor', string),
    alert: optionalField('alert', string),
    attachmentBackground: optionalField('attachmentBackground', string),
    brandAccent: optionalField('brandAccent', string),
    brandPrimary: optionalField('brandPrimary', string),
    brandSecondary: optionalField('brandSecondary', string),
    chatButtonIcon: optionalField('chatButtonIcon', string),
    chatLauncher: optionalField('chatLauncher', string),
    greetingCloseButton: optionalField('greetingCloseButton', string),
    linkColor: optionalField('linkColor', string),
    productImageBackgroundColor: optionalField('productImageBackgroundColor', string),
    submitArrow: optionalField('submitArrow', string),
  }));
}

function validV2Fonts(value: unknown): V2Fonts {
  return object(value, () => ({
    primary: optionalField('primary', string),
    secondary: optionalField('secondary', string),
  }));
}

function validV2Format(value: unknown): V2Format {
  return object(value, () => ({
    date: optionalField('date', (d) => (d === null ? null : string(d))),
    time: optionalField('time', string),
  }));
}

function validV2Header(value: unknown): V2Header {
  return object(value, () => ({
    backgroundColor: optionalField('backgroundColor', string),
    fontColor: optionalField('fontColor', string),
    headingText: optionalField('headingText', string),
    logo: optionalField('logo', string),
    subheadingText: optionalField('subheadingText', string),
    textColor: optionalField('textColor', string),
  }));
}

function validV2LauncherButton(value: unknown): V2LauncherButton {
  const defaultButton = getDefaultV2Configuration().appearance.launcherButton;
  return object(value, () => ({
    text: optionalField('text', string) ?? defaultButton.text,
    type: optionalField('type', among(Object.values(INITIAL_BUTTON_TYPES))) ?? defaultButton.type,
    // Passed through unresolved, like sidebarCollapseAt above: absent has to stay
    // distinguishable from `false` so resolveLauncherButton can keep drawing the
    // header logo for the Enhanced orgs that already had one. deepMerge skips
    // undefined, and the defaults carry no `useLogo`, so the gap survives to it.
    useLogo: optionalField('useLogo', boolean),
  }));
}

function validV2InputChatWindow(value: unknown): V2InputChatWindow {
  const defaultChatWindow = getDefaultV2Configuration().appearance.input.chatWindow;
  return object(value, () => ({
    placeholderText: optionalField('placeholderText', string) ?? defaultChatWindow.placeholderText,
  }));
}

function validV2Input(value: unknown): V2Input {
  const defaultInput = getDefaultV2Configuration().appearance.input;
  return object(value, () => {
    const texts = optionalField('placeholderTexts', array(string)) ?? [];
    const placeholderTexts: [string, ...string[]] =
      texts.length > 0 ? [texts[0], ...texts.slice(1)] : defaultInput.placeholderTexts;
    return {
      chatWindow: optionalField('chatWindow', validV2InputChatWindow) ?? defaultInput.chatWindow,
      placeholderTexts,
    };
  });
}

function validV2ChatMessageConfig(value: unknown): { message: string; title: string } {
  return object(value, () => ({
    message: field('message', string),
    title: field('title', string),
  }));
}

function validV2Messages(value: unknown): V2Messages {
  const defaultMsg = getDefaultV2Configuration().appearance.messages;
  return object(value, () => ({
    chatClosed: optionalField('chatClosed', validV2ChatMessageConfig) ?? defaultMsg.chatClosed,
    chatThrottled: optionalField('chatThrottled', validV2ChatMessageConfig) ?? defaultMsg.chatThrottled,
    consumerMessageBackgroundColor: optionalField('consumerMessageBackgroundColor', string),
    typingIndicatorText: optionalField('typingIndicatorText', string),
  }));
}

function validV2Border(value: unknown): V2Border {
  return object(value, () => ({
    color: field('color', string),
    radius: optionalField('radius', (v) =>
      v === '' ? V2_BORDER_RADII.NONE : among(Object.values(V2_BORDER_RADII))(v),
    ),
    width: field('width', string),
  }));
}

function validV2Tooltips(value: unknown): V2Tooltips {
  return object(value, () => ({
    attachmentIcon: optionalField('attachmentIcon', string),
    chatButton: optionalField('chatButton', string),
    closeWidget: optionalField('closeWidget', string),
    closeWindow: optionalField('closeWindow', string),
    sendIcon: optionalField('sendIcon', string),
  }));
}

function validV2EndChatMessages(value: unknown): V2EndChatMessages {
  return object(value, () => ({
    agentEnded: optionalField('agentEnded', string),
    consumerNoReply: optionalField('consumerNoReply', string),
    newSessionStarted: optionalField('newSessionStarted', string),
    timeExpired: optionalField('timeExpired', string),
  }));
}

function validBorder(value: unknown): Border {
  return object(value, () => ({
    color: field('color', string),
    width: field('width', string),
  }));
}

function validBorderRadius(value: unknown): BorderRadius {
  return among(Object.values(BORDER_RADII))(value);
}

function validAlignment(value: unknown): Alignment {
  return among(Object.values(ALIGNMENTS))(value);
}

function validCloseButton(value: unknown): CloseButton {
  return object(value, () => ({
    iconType: field('iconType', among(Object.values(CLOSE_ICON_TYPE))),
    title: field('title', string),
  }));
}

function validInitialChatButton(value: unknown): InitialChatButton {
  return object(value, () => ({
    type: field('type', among(Object.values(INITIAL_BUTTON_TYPES))),
    value: field('value', string),
  }));
}

function validMinimizedChatButton(value: unknown): MinimizedChatButton {
  return object(value, () => ({
    title: field('title', string),
  }));
}

function validDisplayType(value: unknown): DisplayType {
  return among(Object.values(DISPLAY_TYPE))(value);
}

// An allow-list, so a field left out here never reaches the evaluator -- which is the
// live path for every org, since no production config carries a CHAT_BAR launcher entry.
function validDisplayRuleContent(value: unknown): V2DisplayRuleContent {
  return object(value, () => ({
    matchType: field('matchType', string),
    value: field('value', string),
    // Kept as written. evaluateUrlDisplayRules ignores a choice it does not recognise
    // rather than dropping the rule, so narrowing it here would only move the same
    // decision somewhere the reason for it is less visible.
    display: optionalField('display', string),
  }));
}

function validDisplayRule(value: unknown): DisplayRule {
  return object(value, () => ({
    type: field('type', string),
    id: field('id', string),
    displayDelay: field('displayDelay', number),
    content: field('content', validDisplayRuleContent),
  }));
}

function validDisplayRules(value: unknown): DisplayRules {
  return object(value, () => ({
    showByDefault: field('showByDefault', boolean),
    rules: field('rules', array(validDisplayRule)),
  }));
}

function validChatMessageConfigPartial(value: unknown): Partial<ChatMessageConfig> {
  return object(value, () => ({
    message: optionalField('message', string),
    title: optionalField('title', string),
  }));
}

function validOnboardingConfigPartial(value: unknown): Partial<OnboardingConfig> {
  return object(value, () => ({
    enabled: optionalField('enabled', boolean),
    header: optionalField('header', string),
    introMessage: optionalField('introMessage', string),
    emailField: optionalField('emailField', string),
    nameField: optionalField('nameField', string),
    submitButton: optionalField('submitButton', string),
    authenticationError: optionalField('authenticationError', string),
    blankFieldError: optionalField('blankFieldError', string),
    invalidEmailError: optionalField('invalidEmailError', string),
    initialCompositionStage: optionalField('initialCompositionStage', string),
  }));
}

function validOngoingChatConfigPartial(value: unknown): Partial<OngoingChatConfig> {
  return object(value, () => ({
    title: optionalField('title', string),
    compositionPlaceholder: optionalField('compositionPlaceholder', string),
    submitButtonTitle: optionalField('submitButtonTitle', string),
    welcomeMessage: optionalField('welcomeMessage', string),
    endChatTitle: optionalField('endChatTitle', string),
    endMessageAgentEnded: optionalField('endMessageAgentEnded', string),
    endMessageTimeExpired: optionalField('endMessageTimeExpired', string),
    endMessageConsumerNoReply: optionalField('endMessageConsumerNoReply', string),
    endMessageNewSessionStarted: optionalField('endMessageNewSessionStarted', string),
  }));
}

function validSelfServiceConfig(value: unknown): SelfServiceConfig {
  return object(value, () => ({
    quickActions: field('quickActions', array(unknownLike)),
  }));
}

function validThrottlingConfig(value: unknown): ThrottlingConfig {
  return object(value, () => ({
    percentageRollout: field('percentageRollout', number),
    autoThrottlingEnabled: field('autoThrottlingEnabled', boolean),
    autoThrottlingThreshold: field('autoThrottlingThreshold', number),
  }));
}

function validWidgetType(value: unknown): WidgetType {
  return among(Object.values(WIDGET_TYPES))(value);
}

function validThemeColors(value: unknown): ThemeColors {
  return object(value, () => ({
    alert: optionalField('alert', string),
    attachmentBackground: optionalField('attachmentBackground', string),
    brandAccent: optionalField('brandAccent', string),
    brandPrimary: optionalField('brandPrimary', string),
    brandSecondary: optionalField('brandSecondary', string),
    chatLauncher: optionalField('chatLauncher', string),
    greetingCloseButton: optionalField('greetingCloseButton', string),
    productImageBackgroundColor: optionalField('productImageBackgroundColor', string),
    submitArrow: optionalField('submitArrow', string),
  }));
}

function validFontLibrary(value: unknown): FontLibrary {
  return object(value, () => ({
    primary: field('primary', string),
    secondary: field('secondary', string),
  }));
}

function validThemeFormats(value: unknown): ThemeFormats {
  return object(value, () => ({
    time: optionalField('time', string),
    date: optionalField('date', (v) => {
      if (v === null) {
        return null;
      }
      return string(v);
    }),
  }));
}

function validCustomTextHeader(value: unknown): { greeting?: string; title?: string } {
  return object(value, () => ({
    greeting: optionalField('greeting', string),
    title: optionalField('title', string),
  }));
}

function validCustomTextInteractiveOnboarding(value: unknown): { emailPrompt?: string } {
  return object(value, () => ({
    emailPrompt: optionalField('emailPrompt', string),
  }));
}

function validCustomText(value: unknown): {
  header?: { greeting?: string; title?: string };
  interactiveOnboarding?: { emailPrompt?: string };
} {
  return object(value, () => ({
    header: optionalField('header', validCustomTextHeader),
    interactiveOnboarding: optionalField('interactiveOnboarding', validCustomTextInteractiveOnboarding),
  }));
}

function validPrivacyDisclaimer(value: unknown): PrivacyDisclaimer {
  return object(value, () => ({
    isActivated: field('isActivated', boolean),
    // `text` (32 KB), not `string` (1024). A body over the cap does not fail
    // softly: it rejects the whole server configuration, so the widget never
    // loads. Nothing on the write path holds the V2 body under 1024. The admin
    // model validates it with @IsString/@IsNotEmpty and no @MaxLength, the admin
    // editor sets no limit, and supernova's WidgetConfiguration.Validate() never
    // reaches V2Appearance.PrivacyDisclaimer. (The top-level field is different:
    // supernova rejects a save over 199 plain-text characters, or one carrying a
    // tag outside its allow-list; it stores what it was sent.) Same cap the
    // message-content validator uses for bodies. sc-261601.
    disclaimerBody: field('disclaimerBody', text),
  }));
}

function validProactiveChatCampaign(value: unknown): ProactiveChatCampaign {
  // ProactiveChatCampaign is defined as [key: string]: unknown
  // Use record validator to validate it's an object with string keys
  return record(unknownLike)(value);
}

function validProactiveChatConfig(value: unknown): ProactiveChatConfig {
  return object(value, () => ({
    campaigns: field('campaigns', array(validProactiveChatCampaign)),
  }));
}

function validGAConfiguration(value: unknown): GAConfiguration {
  return object(value, () => ({
    autoDetectTrackingId: optionalField('autoDetectTrackingId', boolean),
    enabled: optionalField('enabled', boolean),
    gaTrackingId: optionalField('gaTrackingId', string),
  }));
}

function validAnalyticsAttribution(value: unknown): AnalyticsAttribution {
  return object(value, () => ({
    gaConfiguration: optionalField('gaConfiguration', validGAConfiguration),
  }));
}

function validTheme(value: unknown): Theme {
  return object(value, () => ({
    borderRadius: optionalField('borderRadius', validBorderRadius),
    border: optionalField('border', validBorder),
    colors: optionalField('colors', validThemeColors),
    dropshadowEnabled: optionalField('dropshadowEnabled', boolean),
    fontLibrary: optionalField('fontLibrary', validFontLibrary),
    formats: optionalField('formats', validThemeFormats),
  }));
}

function validRule(value: unknown): LegacyRule | TranslatedRule {
  // Both LegacyRule and TranslatedRule are objects with optional fields
  // LegacyRule can have: delay, displayDelay, openDelay, proactive, type, message, [key: string]: unknown
  // TranslatedRule can have: displayDelay (required), openDelay, type, message, [key: string]: unknown
  // We validate the known fields exist and have correct types, then return the original value
  // since both types allow [key: string]: unknown to preserve extra fields
  return object(value, () => ({
    delay: optionalField('delay', number),
    displayDelay: optionalField('displayDelay', number),
    openDelay: optionalField('openDelay', number),
    proactive: optionalField('proactive', boolean),
    type: optionalField('type', string),
    message: optionalField('message', string),
  }));
}

function validRules(value: unknown): Rules {
  return record(validRule)(value);
}

function unknownLike(value: unknown): unknown {
  return value;
}

function getDefaultOnboardingConfig(): OnboardingConfig {
  return {
    enabled: true,
    header: "Let's Chat",
    introMessage: 'Hi there! Tell us a bit about yourself.',
    emailField: 'Your email',
    nameField: 'Your name',
    submitButton: "Let's Go",
    authenticationError: 'An error occurred. Please try again.',
    blankFieldError: 'Both name and email are required.',
    invalidEmailError: 'Please enter a valid email address.',
    initialCompositionStage: 'EMAIL',
  };
}

function getDefaultOngoingChatConfig(): OngoingChatConfig {
  return {
    title: "Let's Chat",
    compositionPlaceholder: 'Write a message',
    submitButtonTitle: 'Send message',
    welcomeMessage: 'Hello <<USERNAME>>! How can we help?',
    endChatTitle: 'Chat has ended',
    endMessageAgentEnded: 'Thanks for chatting <<USERNAME>>! More questions? Chat again to pick up where we left off.',
    endMessageTimeExpired: 'Thanks for chatting <<USERNAME>>! More questions? Chat again to pick up where we left off.',
    endMessageConsumerNoReply:
      "We haven't heard from you in a while, <<USERNAME>>. Type another message to pick up where we left off.",
    endMessageNewSessionStarted:
      'We noticed you have another active chat. You can send us a message to chat here, and we will end the other chat for you',
  };
}

function getDefaultChatClosedConfig(): ChatMessageConfig {
  return {
    message:
      "We want to chat with you, but it's outside of our office hours. Chat again tomorrow to pick up where we left off.",
    title: 'Whoops, sorry we missed you!',
  };
}

function getDefaultChatThrottledConfig(): ChatMessageConfig {
  return {
    message:
      'We want to chat with you, but we are experiencing higher chat volumes than usual. Please visit our contact page for other ways to reach out to us.',
    title: 'Whoops, sorry we missed you!',
  };
}

function mergeOnboardingConfig(partial: Partial<OnboardingConfig> | undefined): OnboardingConfig {
  const defaults = getDefaultOnboardingConfig();
  if (!partial) {
    return defaults;
  }
  return {
    ...defaults,
    ...partial,
  };
}

function mergeOngoingChatConfig(partial: Partial<OngoingChatConfig> | undefined): OngoingChatConfig {
  const defaults = getDefaultOngoingChatConfig();
  if (!partial) {
    return defaults;
  }
  return {
    ...defaults,
    ...partial,
  };
}

function mergeChatMessageConfig(
  partial: Partial<ChatMessageConfig> | undefined,
  defaults: ChatMessageConfig,
): ChatMessageConfig {
  if (!partial) {
    return defaults;
  }
  return {
    ...defaults,
    ...partial,
  };
}
